import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Chip,
  TextField,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import { useNavbar } from "../context/NavbarContext";
import { useSelector } from 'react-redux';
import { getAllUsers, updateUserRole, logActivity, deleteUser } from "../api/apiService";

const UserAccessPage = () => {
  const { isNavbarOpen } = useNavbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const currentUser = useSelector((state) => state.auth.user);
  const canEditAccess = currentUser?.role === 'Admin';
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  // console.log("Fetched Current site:", currentUser.email);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        console.log('[UserAccessPage] Fetching users...');
        const fetchedUsers = await getAllUsers();
        console.log('[UserAccessPage] Fetched users:', fetchedUsers);
        
        // Ensure fetchedUsers is an array
        if (Array.isArray(fetchedUsers)) {
          setUsers(fetchedUsers);
        } else {
          console.error('[UserAccessPage] Fetched data is not an array:', fetchedUsers);
          setUsers([]);
        }
      } catch (error) {
        console.error('[UserAccessPage] Error fetching users:', error);
        console.error('[UserAccessPage] Error details:', error.message);
        setUsers([]); // Ensure users is always an array
      } finally {
        setLoadingUsers(false);
      }
    };
  
    fetchUsers();
  }, []);

  // Filter Users by Role and Search
  useEffect(() => {
    console.log('[UserAccessPage] Filtering users. Total users:', users.length, 'Role filter:', selectedRole, 'Search:', searchQuery);
    
    // Ensure users is an array before filtering
    if (!Array.isArray(users)) {
      console.error('[UserAccessPage] users is not an array:', users);
      setFilteredUsers([]);
      return;
    }
    
    let result = (selectedRole === "All")
      ? users
      : users.filter((user) => user.role === selectedRole);
    
    if (searchQuery) {
      result = result.filter((user) =>
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    console.log('[UserAccessPage] Filtered users count:', result.length);
    setFilteredUsers(result);
  }, [selectedRole, users, searchQuery]);

  // Open Confirmation Dialog
  const handleOpenDialog = (user, role) => {
    if (!canEditAccess) return;
    setSelectedUser(user);
    setNewRole(role);
    setOpenDialog(true);
  };

  // Close Dialog
  const handleCloseDialog = () => {
    setSelectedUser(null);
    setNewRole("");
    setOpenDialog(false);
  };

  // Confirm and update role
  const handleConfirmRoleChange = async () => {
    if (!canEditAccess) {
      handleCloseDialog();
      return;
    }
    if (selectedUser && newRole) {
      try {
        console.log('Updating role for user:', {
          email: selectedUser.email,
          newRole: newRole
        });
        
        // Update the UI immediately
        const updatedUsers = users.map(user => 
          user.email === selectedUser.email 
            ? { ...user, role: newRole }
            : user
        );
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
        
        // Update the database
        await updateUserRole(selectedUser.email, newRole);
        await logActivity(currentUser.email, `Changed role to ${newRole} for user ${selectedUser.email}`);
        
        // Refresh the users list to ensure sync with database
        const dbUsers = await getAllUsers();
        setUsers(dbUsers);
        setFilteredUsers(dbUsers);
      } catch (error) {
        console.error('Error updating user role:', error);
        // Revert the UI if the database update fails
        const dbUsers = await getAllUsers();
        setUsers(dbUsers);
        setFilteredUsers(dbUsers);
      }
    }
    handleCloseDialog();
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (user) => {
    if (!canEditAccess) return;
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const handleDeleteDialogClose = () => {
    setUserToDelete(null);
    setDeleteDialogOpen(false);
  };

  // Delete user
  const handleDelete = async () => {
    if (!canEditAccess || !userToDelete) return;

    try {
      console.log('Deleting user:', userToDelete.email);
      
      // Update UI immediately
      const updatedUsers = users.filter(user => user.email !== userToDelete.email);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      
      // Delete from both DynamoDB and Firebase
      await deleteUser(userToDelete.email);
      await logActivity(currentUser.email, `Deleted user ${userToDelete.email}`);
      
      // Refresh from database to ensure sync
      const dbUsers = await getAllUsers();
      setUsers(dbUsers);
      setFilteredUsers(dbUsers);
    } catch (error) {
      console.error('Error deleting user:', error);
      // Revert UI if deletion fails
      const dbUsers = await getAllUsers();
      setUsers(dbUsers);
      setFilteredUsers(dbUsers);
    }
    handleDeleteDialogClose();
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: (theme) => theme.palette.background.default,
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '65px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        {/* <Typography variant="h4" gutterBottom>
          User Access
        </Typography> */}
        {/* Page Header */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: { xs: 2, sm: 3, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PeopleIcon sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main, fontSize: { xs: 24, sm: 28, md: 32 } }} />
            <Typography 
              variant="h4" 
              fontWeight="600"
              sx={{ 
                color: (theme) => theme.palette.text.primary,
                letterSpacing: '-0.5px',
                position: 'relative',
                fontSize: {
                  xs: '1.5rem',
                  sm: '1.75rem',
                  md: '2rem',
                  lg: '2.25rem',
                  xl: '2.5rem'
                }
              }}
            >
              User Access Management
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary, ml: { xs: 0, sm: 5 } }}>
            {canEditAccess
              ? 'Manage roles, search users, and remove accounts.'
              : 'View-only: browse users and roles. Changes require Admin access.'}
          </Typography>
        </Box>

        {/* Role Filter Chips & Search */}
        <Paper elevation={0} sx={{ mb: 2, p: { xs: 1.5, sm: 2 }, borderRadius: '12px', border: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: (theme) => theme.palette.background.paper, boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 10px rgba(0,0,0,0.05)' }}>
          {loadingUsers ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 2 }}>
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
            </Box>
          ) : (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            width: '100%'
          }}>
            {['All','Admin','Manager','Engineer','User'].map((role) => (
              <Chip
                key={role}
                label={role}
                clickable
                aria-label={`Filter role ${role}`}
                sx={{
                  fontWeight: '500',
                  borderRadius: '8px',
                  backgroundColor: (theme) => selectedRole === role 
                    ? theme.palette.brand?.royalBlue || theme.palette.primary.main
                    : (theme.palette.mode === 'dark' ? alpha(theme.palette.text.primary, 0.06) : theme.palette.grey[100]),
                  color: (theme) => selectedRole === role 
                    ? '#FFFFFF'
                    : theme.palette.text.secondary,
                  '&:hover': { 
                    backgroundColor: (theme) => selectedRole === role 
                      ? theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                        : alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.90)
                      : (theme.palette.mode === 'dark' ? alpha(theme.palette.text.primary, 0.10) : theme.palette.grey[200]) 
                  },
                  '& .MuiChip-label': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }
                }}
                onClick={() => setSelectedRole(role)}
              />
            ))}
            <Box sx={{ ml: 'auto', width: { xs: '100%', sm: '250px', md: '350px' } }}>
              <TextField
                label="Search User ID"
                aria-label="Search User ID"
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover fieldset': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              />
            </Box>
          </Box>
          )}
        </Paper>

        {/* Users Table */}
        <TableContainer 
          component={Paper} 
          elevation={2} 
          sx={{ 
            borderRadius: "12px", 
            overflow: 'hidden', 
            boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 10px rgba(0,0,0,0.05)',
            backgroundColor: (theme) => theme.palette.background.paper,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            overflowX: 'auto'
          }}
        >
          {loadingUsers ? (
            <Box sx={{ p: 2, width: '100%' }}>
              <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
              {[...Array(6)].map((_, idx) => (
                <Skeleton key={idx} variant="rectangular" height={isMobile ? 36 : 52} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : (
          <Table 
            stickyHeader 
            size={isMobile ? "small" : "medium"}
            sx={{ minWidth: 650 }}
            aria-label="User access table"
          >
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : theme.palette.grey[50] }}>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  fontSize: {
                    xs: '0.75rem',
                    sm: '0.8rem',
                    md: '0.875rem'
                  }, 
                  color: (theme) => theme.palette.text.secondary 
                }}>
                  Name
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  fontSize: {
                    xs: '0.75rem',
                    sm: '0.8rem',
                    md: '0.875rem'
                  }, 
                  color: (theme) => theme.palette.text.secondary 
                }}>
                  Email
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  fontSize: {
                    xs: '0.75rem',
                    sm: '0.8rem',
                    md: '0.875rem'
                  }, 
                  color: (theme) => theme.palette.text.secondary 
                }}>
                  Role
                </TableCell>
                {canEditAccess && (
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  fontSize: {
                    xs: '0.75rem',
                    sm: '0.8rem',
                    md: '0.875rem'
                  }, 
                  color: (theme) => theme.palette.text.secondary 
                }}>
                  Actions
                </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEditAccess ? 4 : 3} align="center">
                    <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary, py: 2 }}>No users found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.email} hover sx={{ '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.10 : 0.06) } }}>
                  <TableCell sx={{ 
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.8rem',
                      md: '0.875rem'
                    }, 
                    color: (theme) => theme.palette.text.primary 
                  }}>
                    {user.name || '-'}
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.8rem',
                      md: '0.875rem'
                    }, 
                    color: (theme) => theme.palette.text.primary 
                  }}>
                    {user.email}
                  </TableCell>
                  <TableCell>
                    {canEditAccess ? (
                    <Select
                      value={user.role}
                      onChange={(e) => handleOpenDialog(user, e.target.value)}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        backgroundColor: (theme) => theme.palette.background.paper,
                        borderRadius: "8px",
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => theme.palette.divider },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main },
                        width: {
                          xs: "100px",
                          sm: "120px",
                          md: "120px"
                        },
                        '& .MuiSelect-select': {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.8rem',
                            md: '0.875rem'
                          },
                          color: (theme) => theme.palette.text.primary,
                        },
                        '& .MuiSelect-icon': {
                          color: (theme) => theme.palette.text.secondary,
                        }
                      }}
                    >
                      {['Admin', 'Manager', 'Engineer', 'User'].map(r => (
                        <MenuItem 
                          key={r} 
                          value={r}
                          sx={{
                            fontSize: {
                              xs: '0.75rem',
                              sm: '0.8rem',
                              md: '0.875rem'
                            }
                          }}
                        >
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                    ) : (
                      <Chip
                        label={user.role || 'User'}
                        size="small"
                        sx={{ fontWeight: 500, borderRadius: '8px' }}
                      />
                    )}
                  </TableCell>
                  {canEditAccess && (
                  <TableCell>
                    <IconButton 
                      onClick={() => handleDeleteClick(user)} 
                      size="small"
                      aria-label={`Delete user ${user.email}`}
                      sx={{ 
                        color: (theme) => theme.palette.error.main, 
                        "&:hover": { backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                        '&:focus-visible': {
                          outline: (theme) => `2px solid ${theme.palette.error.main}`,
                          outlineOffset: '2px',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  )}
                </TableRow>
              )))}
            </TableBody>
          </Table>
          )}
        </TableContainer>

        {/* Confirmation Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: '16px',
              width: {
                xs: '90%',
                sm: '400px',
                md: '500px'
              },
              m: 2
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: "bold",
            color: (theme) => theme.palette.text.primary,
            fontSize: {
              xs: '1rem',
              sm: '1.25rem',
              md: '1.5rem'
            }
          }}>
            Confirm Role Change
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ 
              fontSize: {
                xs: '0.75rem',
                sm: '0.8rem',
                md: '0.875rem'
              }, 
              color: (theme) => theme.palette.text.secondary 
            }}>
              Are you sure you want to change <b>{selectedUser?.email}</b>'s role to <b>{newRole}</b>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDialog} 
              sx={{ 
                color: (theme) => theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                },
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.8rem',
                  md: '0.875rem'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmRoleChange} 
              sx={{ 
                backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main, 
                color: '#FFFFFF', 
                "&:hover": { 
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                    : alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.90)
                },
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.8rem',
                  md: '0.875rem'
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={handleDeleteDialogClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: '16px',
              width: {
                xs: '90%',
                sm: '400px',
                md: '500px'
              },
              m: 2
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: "bold",
            color: (theme) => theme.palette.text.primary,
            fontSize: {
              xs: '1rem',
              sm: '1.25rem',
              md: '1.5rem'
            }
          }}>
            Confirm User Deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ 
              fontSize: {
                xs: '0.75rem',
                sm: '0.8rem',
                md: '0.875rem'
              }, 
              color: (theme) => theme.palette.text.secondary 
            }}>
              Are you sure you want to delete user <b>{userToDelete?.email}</b>? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleDeleteDialogClose} 
              sx={{ 
                color: (theme) => theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                },
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.8rem',
                  md: '0.875rem'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              sx={{ 
                backgroundColor: (theme) => theme.palette.error.main, 
                color: '#FFFFFF', 
                "&:hover": { 
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.error.main, 0.85)
                    : alpha(theme.palette.error.main, 0.90)
                },
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.8rem',
                  md: '0.875rem'
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default UserAccessPage;
